import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const ACCENT = '#4d9fff';

const Container = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem 1.75rem 2rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 22% 18%, rgba(77, 159, 255, 0.18), transparent 48%),
      radial-gradient(circle at 78% 22%, rgba(77, 159, 255, 0.1), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 2rem 1.1rem 1.6rem;
    margin-bottom: 1.5rem;
    border-radius: 18px;
  }
`;

const Title = styled.h1`
  font-size: 2.6rem;
  color: rgb(var(--fg) / 0.96);
  margin: 0;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.03em;

  @media (max-width: 600px) {
    font-size: 1.9rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  font-weight: 400;
  color: rgb(var(--fg) / 0.72);
  margin: 14px auto 14px;
  line-height: 1.65;
  max-width: 720px;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const DatePublished = styled.p`
  font-size: 0.82rem;
  color: rgb(var(--gray) / 0.6);
  margin: 0 0 18px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  font-size: 0.78rem;
  padding: 6px 13px;
  border-radius: 999px;
  font-weight: 600;
  border: 1px solid rgba(77, 159, 255, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s, border-color 0.2s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.08);
    border-color: rgba(77, 159, 255, 0.55);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
  padding: 1.75rem 1.6rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 600px) {
    margin-bottom: 1.1rem;
    padding: 1.25rem 1.05rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  color: ${ACCENT};
  margin: 0 0 14px;
  border-bottom: 1px solid rgba(77, 159, 255, 0.25);
  padding-bottom: 10px;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.15rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin: 0 0 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${ACCENT};
    text-decoration: none;
    border-bottom: 1px solid rgba(77, 159, 255, 0.35);
    transition: border-color 0.2s;
  }

  a:hover {
    border-bottom-color: ${ACCENT};
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 1.5rem;
  line-height: 1.7;
  color: rgb(var(--fg) / 0.82);
  font-size: 1rem;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.7em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(77, 159, 255, 0.7);
    box-shadow: 0 0 8px rgba(77, 159, 255, 0.45);
  }

  strong {
    color: rgb(var(--fg) / 0.95);
    font-weight: 700;
  }

  a {
    color: ${ACCENT};
    text-decoration: none;
    border-bottom: 1px solid rgba(77, 159, 255, 0.3);
    transition: border-color 0.2s;
  }

  a:hover {
    border-bottom-color: ${ACCENT};
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.1rem;
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ResultCard = styled.div`
  background: rgb(var(--fg) / 0.03);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
`;

const ResultImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  display: block;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgb(var(--fg) / 0.06);
  filter: brightness(0.92) saturate(0.95);
`;

const ResultTitle = styled.h4`
  font-size: 1rem;
  margin: 0.95rem 0 0.35rem;
  color: rgb(var(--fg) / 0.95);
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const ResultText = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.6;
  color: rgb(var(--fg) / 0.72);
`;

const EmbedFrame = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 16px;
  background: rgb(0 0 0 / calc(0.42 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.06);

  iframe {
    display: block;
    width: 100%;
    border: 0;
    border-radius: 10px;
    background: #0a0d12;
  }
`;

const EmbedLabel = styled.p`
  margin: 0 0 0.55rem;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${ACCENT};
  font-weight: 700;
`;

const EmbedCaption = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  color: rgb(var(--fg) / 0.78);
  line-height: 1.6;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 0.5rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 2.2rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(77, 159, 255, 0.5);
  }
`;

const results = [
  {
    title: 'Demographic Dashboard',
    desc: 'Lung cancer incidence across countries, with age and gender distributions.',
    src: '/images/projects/bbda_bi_1.png',
    alt: 'Demographic Dashboard',
  },
  {
    title: 'Smoker Analysis',
    desc: 'Smoking-status impact on cancer rates, filtered by diagnosis year and age.',
    src: '/images/projects/bbda_bi_2.png',
    alt: 'Smoker Analysis',
  },
  {
    title: 'Treatment Dashboard',
    desc: 'Survival rates correlated with treatment types and cancer stages.',
    src: '/images/projects/bbda_bi_3.png',
    alt: 'Treatment Dashboard',
  },
  {
    title: 'Country Deep-Dive',
    desc: 'In-depth view of incidents for any selected country.',
    src: '/images/projects/bbda_bi_4.png',
    alt: 'Greece Dashboard',
  },
];

const BbdaInteractiveDashboard = () => {
  const tags = ['Python', 'Power BI', 'Data Visualization', 'Dashboards'];
  const datePublished = '2024-05-25';
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <Title>Lung Cancer Mortality Dashboard</Title>
        <Subtitle>
          A set of interactive dashboards surfacing lung cancer statistics across demographics,
          smokers, treatments, and country-specific trends.
        </Subtitle>
        <DatePublished>Published on {datePublished}</DatePublished>
        <CoverImage src="/images/bbda_bi.png" alt="Demographic Dashboard" />
        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>

      <Section>
        <SectionTitle>Collaborators</SectionTitle>
        <List>
          <ListItem>
            <a href="https://www.linkedin.com/in/aliki-koutsodonti-906390166/" target="_blank" rel="noopener noreferrer">
              Aliki Koutsodonti
            </a>
          </ListItem>
          <ListItem>
            <a href="https://www.linkedin.com/in/konstantinos-lamprou-943038213/" target="_blank" rel="noopener noreferrer">
              Konstantinos Lamprou
            </a>
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <Paragraph>
          This project was conducted as part of the Data Science Bootcamp from Big Blue Data Academy.
          It was the second practice project of the course, and was completed in around 4 hours on the 1st of June 2024. It uses
          tools such as Dash, Plotly, and Power BI to create interactive dashboards. The aim was to present critical lung
          cancer statistics across various countries, including smoking analysis, treatment outcomes, and trends within
          Greece. These dashboards provide interactive insights into the dataset, allowing users to filter data
          dynamically and visualize relationships between different factors.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Objectives</SectionTitle>
        <Paragraph>The primary objectives of this project were:</Paragraph>
        <List>
          <ListItem>To explore key demographics and health factors in lung cancer mortality rates.</ListItem>
          <ListItem>To present detailed analyses of the impact of smoking on cancer incidence.</ListItem>
          <ListItem>To analyze the relationship between treatment types and patient outcomes.</ListItem>
          <ListItem>To provide specific insights into lung cancer cases within Greece.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Tools Used</SectionTitle>
        <Paragraph>The following tools and libraries were utilized in this project:</Paragraph>
        <List>
          <ListItem><strong>Python</strong></ListItem>
          <ListItem><strong>Pandas</strong> and <strong>NumPy</strong> for data manipulation</ListItem>
          <ListItem><strong>Plotly</strong> and <strong>Dash</strong> for interactive dashboards</ListItem>
          <ListItem><strong>Power BI</strong> for additional visualizations</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Methodology</SectionTitle>
        <Paragraph>
          The dataset includes demographic data, smoking status, treatment types, and country-specific information. The
          interactive dashboard uses dynamic filtering and plotting to allow users to adjust variables such as age,
          cancer stage, and family history. Additionally, the dashboards display insights about smokers and their impact
          on cancer incidences, treatment outcomes, and patient survival rates.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Results</SectionTitle>
        <Paragraph>
          This project produced interactive insights into the lung cancer dataset, revealing significant patterns:
        </Paragraph>
        <ResultsGrid>
          {results.map((r) => (
            <ResultCard key={r.title}>
              <ResultImage src={r.src} alt={r.alt} />
              <ResultTitle>{r.title}</ResultTitle>
              <ResultText>{r.desc}</ResultText>
            </ResultCard>
          ))}
        </ResultsGrid>

        <EmbedFrame>
          <EmbedLabel>Interactive Notebook</EmbedLabel>
          <EmbedCaption>
            Dash &amp; Plotly dashboard exploring correlations between risk factors and lung cancer treatment outcomes.
          </EmbedCaption>
          <iframe
            src="/embeds/dash_lung_cancer_correlation.html"
            width="100%"
            height="800"
            title="Dash Notebook"
          />
        </EmbedFrame>
      </Section>

      <Section>
        <SectionTitle>Conclusion</SectionTitle>
        <Paragraph>
          The project successfully created a set of interactive dashboards that provide an in-depth analysis of lung
          cancer mortality data. These dashboards allow users to explore relationships between smoking, treatment types,
          and survival rates in a visually intuitive way.
        </Paragraph>
      </Section>

      <ButtonRow>
        <BackButton onClick={() => navigate('/projects')}>← Back to Projects</BackButton>
      </ButtonRow>
    </Container>
  );
};

export const BbdaInteractiveDashboardMetadata = {
  title: 'Lung Cancer Mortality Interactive Dashboard',
  description:
    'Created interactive dashboards presenting lung cancer mortality data across demographics, smokers, treatments, and Greece-specific trends.',
  tags: ['Python', 'Power BI', 'Data Visualization', 'Dashboards'],
  languages: ['Python', 'PowerBI', 'GitHub'],
  datePublished: '2024-05-25',
  path: '/projects/bbda-interactive-dashboard',
  coverImage: '/images/bbda_bi.png',
};

export default BbdaInteractiveDashboard;
